/**
 * Cross-page-load guard for AUTOMATIC `signinRedirect()` calls.
 *
 * The `hasTriedSignin` ref in {@link AuthController} only prevents redirect
 * loops WITHIN one page load — it resets on every navigation. When a failure
 * recurs on every Keycloak callback (persistent realm `server_error`, or iOS
 * WebKit storage partitioning losing the OIDC state on every return —
 * CUSTOMER-UI-3T), each fresh page load would redirect again: an unbounded,
 * machine-speed app↔Keycloak loop that also fires one Sentry event per cycle.
 *
 * This module caps automatic redirects per sessionStorage window. Past the
 * cap the app stays on the splash screen, whose "Reload app" button (shown
 * after 8s) is the manual recovery path — it calls
 * {@link clearSigninRedirectAttempts} so an intentional user action can
 * always retry. A successful authentication also clears the counter.
 *
 * The cap is deliberately loose (3 redirects / 60s): a legitimate signed-out
 * bounce to Keycloak involves human-speed interaction and never hits it;
 * a broken-callback loop hits it within seconds. If sessionStorage is
 * unavailable (private mode / partitioned), the guard degrades to the
 * per-load `hasTriedSignin` behaviour rather than blocking sign-in.
 */
import { logError } from "@/lib/logError";

const STORAGE_KEY = "auth.signinRedirectAttempts";
const ATTEMPT_WINDOW_MS = 60_000;
const MAX_ATTEMPTS_PER_WINDOW = 3;

function readRecentAttempts(now: number): number[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (ts): ts is number =>
        typeof ts === "number" && now - ts < ATTEMPT_WINDOW_MS
    );
  } catch {
    // Storage unavailable or corrupted — degrade to the per-load guard.
    return [];
  }
}

/** True once the automatic-redirect budget for the current window is spent. */
export function isSigninRedirectExhausted(now: number = Date.now()): boolean {
  return readRecentAttempts(now).length >= MAX_ATTEMPTS_PER_WINDOW;
}

export interface SigninRedirectAttemptStats {
  /** Automatic redirects recorded inside the current window. */
  attemptsInWindow: number;
  /** Smallest gap between consecutive attempts, or null with fewer than two. */
  minGapMs: number | null;
}

/**
 * Raw pace evidence for the budget-exhausted report: how many automatic
 * redirects sit in the window and how close together the nearest two were.
 * Reported as-is (no level change) — with Amplify cold starts and a Keycloak
 * render in every cycle, a machine loop and a person bouncing between pages
 * can both exceed several seconds per cycle, so a threshold would mislead.
 */
export function getSigninRedirectAttemptStats(
  now: number = Date.now()
): SigninRedirectAttemptStats {
  const attempts = [...readRecentAttempts(now)].sort((a, b) => a - b);
  let minGapMs: number | null = null;
  for (let i = 1; i < attempts.length; i += 1) {
    const previous = attempts[i - 1];
    const current = attempts[i];
    if (previous === undefined || current === undefined) continue;
    const gap = current - previous;
    minGapMs = minGapMs === null ? gap : Math.min(minGapMs, gap);
  }
  return { attemptsInWindow: attempts.length, minGapMs };
}

/**
 * True when an automatic signinRedirect was initiated within the last
 * `withinMs`. The splash watchdog uses this to DOWNGRADE its "splash stuck"
 * report to a warning: a stuck splash *while a Keycloak redirect is in flight*
 * is the slow-but-healthy auth round-trip (CUSTOMER-UI-3Z) that self-heals when
 * the callback returns — not a genuine wedge. The truly-parked state (budget
 * spent, no redirect possible) is reported separately as the
 * redirect-budget-exhausted event in AuthController.
 *
 * Window default 15s comfortably brackets the 8s stuck-detection delay: the
 * redirect is recorded at auth-resolution (within a few seconds of page load),
 * so it's still "recent" when the watchdog fires.
 */
export function isSigninRedirectInFlight(
  withinMs = 15_000,
  now: number = Date.now()
): boolean {
  const attempts = readRecentAttempts(now);
  if (attempts.length === 0) return false;
  const mostRecent = Math.max(...attempts);
  return now - mostRecent < withinMs;
}

/** Record that an automatic signinRedirect was just initiated. */
export function recordSigninRedirectAttempt(now: number = Date.now()): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify([...readRecentAttempts(now), now])
    );
  } catch (error) {
    // Storage unavailable (private mode etc.) — the guard degrades to the
    // per-load hasTriedSignin latch; log for triage, never block sign-in.
    logError("[Auth] Could not record signin redirect attempt", error);
  }
}

/** Reset the budget — call on successful auth or explicit user retry. */
export function clearSigninRedirectAttempts(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}
